"use client";

import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/dropdown";
import { Button } from "@heroui/button";
import { useRouter, usePathname } from "next/navigation";
import US from "country-flag-icons/react/3x2/US";
import KR from "country-flag-icons/react/3x2/KR";
import JP from "country-flag-icons/react/3x2/JP";

import { languages, languageCodes, type LanguageCode } from "@/config/i18n";
import { setLanguageCookie } from "@/lib/languageCookie";
import { createTranslator } from "@/lib/i18n/translations";
import { switchLanguageInPath } from "@/lib/i18n/paths";

const flagComponents = {
  en: US,
  ko: KR,
  ja: JP,
} as const;

function FlagIcon({
  lang,
  className,
}: {
  lang: LanguageCode;
  className?: string;
}) {
  const Flag = flagComponents[lang];

  return <Flag aria-hidden className={className} />;
}

interface LanguageSwitcherProps {
  currentLang: LanguageCode;
}

export function LanguageSwitcher({ currentLang }: LanguageSwitcherProps) {
  const router = useRouter();
  const pathname = usePathname();
  const t = createTranslator(currentLang);

  const handleLanguageChange = (lang: LanguageCode) => {
    if (lang === currentLang) return;

    setLanguageCookie(lang);

    // Keep the current page and its query string (filters, sort, ...)
    const search = typeof window !== "undefined" ? window.location.search : "";

    router.push(`${switchLanguageInPath(pathname, lang)}${search}`);
  };

  return (
    <Dropdown>
      <DropdownTrigger>
        <Button
          aria-label={t("language.switchLanguage")}
          className="gap-2"
          size="md"
          variant="flat"
        >
          <FlagIcon className="w-5 h-4" lang={currentLang} />
          <span className="hidden md:inline">
            {languages[currentLang].name}
          </span>
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={t("language.switchLanguage")}
        selectedKeys={new Set([currentLang])}
        selectionMode="single"
        onAction={(key) => handleLanguageChange(key as LanguageCode)}
      >
        {languageCodes.map((lang) => (
          <DropdownItem
            key={lang}
            startContent={<FlagIcon className="w-5 h-4" lang={lang} />}
            textValue={languages[lang].name}
          >
            {languages[lang].name}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
